import { useState, useEffect, useCallback, useRef } from 'react';
import { useBranch } from '../context/BranchContext';
import { useAuth } from '../context/AuthContext';
import { todayStr, yesterdayStr } from '../services/reportsApi';
import {
    getCustomerVolumeSummary,
    getCustomerByTenure,
    getCustomerLoyaltyCounts,
    getCustomerInfo,
    getCustomerInsightsSummary,
    getCustomerDineIn,
    getCustomerOnline,
    getCustomerOrderHistory,
    getCustomerLatestOrder,
    getCustomerFavouriteItems,
} from '../services/reportsApi';
import { getThisYearRange } from '../utils/dateTimeUtils';

const toDateStr = (date: Date): string => date.toISOString().split('T')[0];

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'idToken', 'id_token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

function ensureArray(val: any): any[] {
    if (Array.isArray(val)) return val;
    if (val && typeof val === 'object' && val.content) return val.content;
    if (val && typeof val === 'object' && val.data) return Array.isArray(val.data) ? val.data : [val.data];
    return [];
}

function ensureObject(val: any): any {
    if (val && typeof val === 'object' && !Array.isArray(val)) return val;
    return {};
}

/** Date range for period (Detailed tab uses start/end) */
export function getCustomerDateRange(period: string): { startDate: string; endDate: string } {
    const raw = typeof period === "string" ? period.trim() : "";
    const normalized = raw.toLowerCase().replace(/\s+/g, "");
    if (raw && raw.startsWith('custom:')) {
        const p = period.split(':');
        if (p.length === 3) return { startDate: p[1], endDate: p[2] };
    }
    const today = new Date();
    const tStr = todayStr();
    const yStr = yesterdayStr();
    if (normalized === 'today') return { startDate: tStr, endDate: tStr };
    if (normalized === 'yesterday') return { startDate: yStr, endDate: yStr };
    if (normalized === '7d') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const d = new Date(yesterday);
        d.setDate(d.getDate() - 6);
        return { startDate: toDateStr(d), endDate: yStr };
    }
    if (normalized === '30d') {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const d = new Date(yesterday);
        d.setDate(d.getDate() - 29);
        return { startDate: toDateStr(d), endDate: yStr };
    }
    if (normalized === 'thismonth' || normalized === 'month') {
        // Month-to-date always ends at TODAY, not yesterday — "This Month" names the
        // current calendar month, so today is part of it (see
        // src/components/reports/ThisMonthDateProdIssue/ThisMonthDateProdIssueBugFix.md).
        const d = new Date(today.getFullYear(), today.getMonth(), 1);
        return { startDate: toDateStr(d), endDate: tStr };
    }
    if (normalized === 'thisyear') {
        // Year-to-date always ends at TODAY — the same rule as 'thismonth' above, one
        // calendar level up (DPFIX-10). Delegated to the shared helper rather than
        // recomputed here so the range this screen FETCHES is identical to the one the
        // date control DISPLAYS via resolveTokenRange('This Year'): getThisYearRange reads
        // the server clock in the outlet's timezone, whereas a local `new Date()` copy
        // could name a different year across a New Year boundary. See
        // src/components/reports/ThisYearDateProdIssue/ThisYearDateProdIssueBugFix.md.
        return getThisYearRange();
    }
    if (normalized === 'lastmonth') {
        const d = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const end = new Date(today.getFullYear(), today.getMonth(), 0);
        return { startDate: toDateStr(d), endDate: toDateStr(end) };
    }
    if (normalized === 'quarter') {
        const q = Math.floor(today.getMonth() / 3) + 1;
        const d = new Date(today.getFullYear(), (q - 1) * 3, 1);
        return { startDate: toDateStr(d), endDate: tStr };
    }
    return { startDate: tStr, endDate: tStr };
}

/**
 * Summary Insights — all 5 APIs when locationId is set.
 * Accepts optional datePeriod to filter by date range.
 */
export function useCustomerSummaryInsights(datePeriod?: string) {
    const { selectedBranch } = useBranch();
    const { auth } = useAuth();
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = findToken(auth);
    const dateRange = datePeriod ? getCustomerDateRange(datePeriod) : null;
    const startDate = dateRange?.startDate;
    const endDate = dateRange?.endDate;

    const [volumeSummary, setVolumeSummary] = useState(null);
    const [byTenure, setByTenure] = useState([]);
    const [loyaltyCounts, setLoyaltyCounts] = useState([]);
    // Start in loading state so the shimmer renders on first paint. Without this,
    // content layers mount briefly before fetchSummary sets loading=true, causing
    // child components to mount → unmount → remount and fire effects twice.
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSummary = useCallback(async () => {
        if (!locationId) {
            setVolumeSummary(null);
            setByTenure([]);
            setLoyaltyCounts([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        const opts = { locationId, token, startDate, endDate };
        try {
            // Only the 3 APIs actually consumed by CustomersDashboardContent:
            //   - volumeSummary → fallback for total-customers count
            //   - byTenure      → NEW CUSTOMERS / RETURNING KPIs
            //   - loyaltyCounts → Customer Segments (RFM) + Segment Breakdown
            // getCustomerTotalSpend / getCustomerAvgCoverSize previously fetched
            // here were never rendered (SummaryInsightsContent is dead code) and
            // have been removed.
            const [vol, tenure, loyalty] = await Promise.all([
                getCustomerVolumeSummary(opts).then(ensureObject).catch(() => ({})),
                getCustomerByTenure(opts).then(ensureArray).catch(() => []),
                getCustomerLoyaltyCounts(opts).then(ensureArray).catch(() => []),
            ]);
            console.log('[CustomersReport] getCustomerLoyaltyCounts (/customer/insights/customer/loyalty-counts):', JSON.stringify(loyalty, null, 2));
            setVolumeSummary(vol?.categories ? { categories: vol.categories } : null);
            setByTenure(Array.isArray(tenure) ? tenure : []);
            setLoyaltyCounts(Array.isArray(loyalty) ? loyalty : []);
        } catch (err) {
            setError(err?.message || 'Failed to load summary insights');
            setVolumeSummary(null);
            setByTenure([]);
            setLoyaltyCounts([]);
        } finally {
            setLoading(false);
        }
    }, [locationId, token, startDate, endDate]);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    return {
        locationId,
        volumeSummary,
        byTenure,
        // byTotalSpend / byAvgCoverSize: no longer fetched (never rendered).
        // Returning empty arrays preserves backwards-compatibility for any
        // callers still destructuring them.
        byTotalSpend: [] as any[],
        byAvgCoverSize: [] as any[],
        loyaltyCounts,
        loading,
        error,
        refetch: fetchSummary,
    };
}

const DEBOUNCE_MS = 1000;

/**
 * Detailed Insights — customer search (debounced), then per-customer APIs when one is selected.
 */
export function useCustomerDetailedInsights(datePeriod: string, orderHistoryPage: number = 1, orderHistorySize: number = 10) {
    const { selectedBranch } = useBranch();
    const { auth } = useAuth();
    const locationId = selectedBranch?.id ?? selectedBranch?.locationId ?? '';
    const token = findToken(auth);
    const { startDate, endDate } = getCustomerDateRange(datePeriod);

    const [searchQuery, setSearchQuery] = useState('');
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const debounceRef = useRef(null);

    const [summary, setSummary] = useState(null);
    const [dineIn, setDineIn] = useState(null);
    const [offPrem, setOffPrem] = useState(null);
    const [orderHistory, setOrderHistory] = useState({ content: [], totalPages: 0, totalElements: 0 });
    const [latestOrder, setLatestOrder] = useState(null);
    const [favouriteItems, setFavouriteItems] = useState([]);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailError, setDetailError] = useState(null);

    const phoneNumber = selectedCustomer?.value ?? selectedCustomer?.phoneNumber ?? '';

    useEffect(() => {
        if (!searchQuery?.trim() || !locationId || !startDate || !endDate) {
            setCustomerOptions([]);
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
                debounceRef.current = null;
            }
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            debounceRef.current = null;
            setSearchLoading(true);
            getCustomerInfo({ locationId, startDate, endDate, search: searchQuery.trim(), page: 1, size: 10, token })
                .then((res) => {
                    const content = res?.content ?? ensureArray(res);
                    setCustomerOptions(
                        content.map((c) => ({
                            label: `${c.customerName || c.fullName || ''} - ${c.phoneNumber || ''}`,
                            value: (c.phoneNumber || '').replace(/^\+/, ''),
                            phoneNumber: c.phoneNumber,
                            customerName: c.customerName || c.fullName,
                        }))
                    );
                })
                .catch(() => setCustomerOptions([]))
                .finally(() => setSearchLoading(false));
        }, DEBOUNCE_MS);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [searchQuery, locationId, startDate, endDate, token]);

    const fetchDetail = useCallback(async () => {
        const digits = (phoneNumber || '').replace(/^\+/, '');
        if (!locationId || !startDate || !endDate || !digits) {
            setSummary(null);
            setDineIn(null);
            setOffPrem(null);
            setOrderHistory({ content: [], totalPages: 0, totalElements: 0 });
            setLatestOrder(null);
            setFavouriteItems([]);
            return;
        }
        setDetailLoading(true);
        setDetailError(null);
        const opts = { locationId, startDate, endDate, phoneNumber: digits, token };
        const orderOpts = { ...opts, page: orderHistoryPage, size: orderHistorySize };
        try {
            const [sumRes, dineRes, offRes, orderRes, latestRes, favRes] = await Promise.all([
                getCustomerInsightsSummary(opts).then(ensureObject).catch(() => null),
                getCustomerDineIn(opts).then(ensureObject).catch(() => null),
                getCustomerOnline(opts).then(ensureObject).catch(() => null),
                getCustomerOrderHistory(orderOpts).then((r) => ({ content: ensureArray(r), totalPages: r?.totalPages ?? 0, totalElements: r?.totalElements ?? 0 })).catch(() => ({ content: [], totalPages: 0, totalElements: 0 })),
                getCustomerLatestOrder(opts).then(ensureObject).catch(() => null),
                getCustomerFavouriteItems(opts).then(ensureArray).catch(() => []),
            ]);
            setSummary(sumRes && Object.keys(sumRes).length ? sumRes : null);
            setDineIn(dineRes && Object.keys(dineRes).length ? dineRes : null);
            setOffPrem(offRes && Object.keys(offRes).length ? offRes : null);
            setOrderHistory(orderRes || { content: [], totalPages: 0, totalElements: 0 });
            setLatestOrder(latestRes && Object.keys(latestRes).length ? latestRes : null);
            setFavouriteItems(Array.isArray(favRes) ? favRes : []);
        } catch (err) {
            setDetailError(err?.message || 'Failed to load customer details');
            setSummary(null);
            setDineIn(null);
            setOffPrem(null);
            setOrderHistory({ content: [], totalPages: 0, totalElements: 0 });
            setLatestOrder(null);
            setFavouriteItems([]);
        } finally {
            setDetailLoading(false);
        }
    }, [locationId, startDate, endDate, phoneNumber, orderHistoryPage, orderHistorySize, token]);

    useEffect(() => {
        fetchDetail();
    }, [fetchDetail]);

    const clearCustomer = useCallback(() => {
        setSelectedCustomer(null);
        setSummary(null);
        setDineIn(null);
        setOffPrem(null);
        setOrderHistory({ content: [], totalPages: 0, totalElements: 0 });
        setLatestOrder(null);
        setFavouriteItems([]);
        setDetailError(null);
    }, []);

    return {
        locationId,
        startDate,
        endDate,
        searchQuery,
        setSearchQuery,
        customerOptions,
        searchLoading,
        selectedCustomer,
        setSelectedCustomer,
        summary,
        dineIn,
        offPrem,
        orderHistory,
        latestOrder,
        favouriteItems,
        detailLoading,
        detailError,
        refetchDetail: fetchDetail,
        clearCustomer,
    };
}
